import type { IndustryTaxonomyIndex } from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import type { GroupedTechnologies } from "app/pages/sales-intelligence/domain/technology/types/model";
import type { FilterUsage } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";
import type { ISiCountryService } from "app/pages/sales-intelligence/services/siCountryService";
import type { SuppressionList } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import type { OpportunityListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import type ISignalsListViewModel from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalsListViewModel";
import type { DemoId } from "app/pages/sales-intelligence/types/demo-mode";

import type { CompanySelectObject, ILeadsSearchDescriptionDto } from "./description.dto";

/**
 * The options a leads filter's model is built from, each fetched asynchronously. A definition lists what it
 * needs (`requires`); the central resolver derives each query's `enabled` from that and reports which have
 * resolved as the `ready` set, so a filter appears the moment its own options land.
 *
 * Deliberately only options data. A flag that decides *which* filter exists is not a build input — it is a
 * precondition of the panel, resolved before render by `onEnterSalesIntelligenceRoot`, so no definition ever
 * has to wait on one.
 */
export const leadsDataSourceMap = {
    description: "description",
    technologies: "technologies",
    industryTaxonomy: "industryTaxonomy",
    suppressionLists: "suppressionLists",
    signals: "signals",
} as const;

export type LeadsDataSource = (typeof leadsDataSourceMap)[keyof typeof leadsDataSourceMap];

/**
 * Everything a leads filter's `build(ctx)` may read: the fetched descriptions and redux-derived collections
 * the models need. Optional fields are absent until their data source is `ready`; a definition that reads
 * one declares the matching `requires` entry, so `build` only runs once it is present. Route seeds are NOT
 * here — they are the route's intent, written by the page-root init (see `useApplyInitialFilters`), not a build input.
 */
export type LeadsFilterBuildContext = {
    readonly description?: ILeadsSearchDescriptionDto;
    readonly technologies?: GroupedTechnologies;
    readonly industryTaxonomy?: IndustryTaxonomyIndex;
    readonly countryService: ISiCountryService;
    readonly opportunityLists: readonly OpportunityListType[];
    readonly suppressionLists: readonly SuppressionList[];
    readonly signalsListViewModel: ISignalsListViewModel;
    readonly hasScoringRules: boolean;
    readonly enabledLeadScoreTiers: ReadonlySet<LeadPriorityScore>;
    readonly listCompanies?: readonly CompanySelectObject[];
    /** Page intent: whether the suppression-list filter defaults to every list selected. */
    readonly applySuppressionListByDefault: boolean;

    /** Session intent: the id of the active demo, or undefined. A filter reacts to its own demo by id. */
    readonly activeDemoId: DemoId | undefined;
    /**
     * User intent: whether this user's account was opted into the new industry classification (ADR-0013).
     * Always settled — the SI root resolves it before any page renders — so it needs no readiness companion.
     * Read it through `modules/industry/utils/industryVisibility`, never directly.
     */
    readonly isIndustryTaxonomyOptedIn: boolean;
    /** Surface intent: this surface's filter usage mode */
    readonly usage: FilterUsage;
};
